import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { CommonModule, Location } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { DxSelectBoxModule } from "devextreme-angular";
import { TranslateModule, TranslateService } from "@ngx-translate/core";

import { BreadcrumbService } from "@app/services/breadcrumb.service";
import { DevextremesharedmoduleModule } from "@app/shared/Module/devextremeshared/devextremeshared.module";
import { HierarchyFilterComponent } from "@app/shared/components/hierarchy-filter/hierarchy-filter.component";
import { FinancialYear } from "@app/classes/domain/entities/website/financialyear/financialyearmaster";
import { SUBMENU_ID } from "@app/classes/domain/constants";
import { DomainEnums, FromEntityTypes, ReportFilterTypes } from "@app/classes/infrastructure/enums";
import { TransportData } from "@app/classes/infrastructure/transportdata";
import { PayloadPacketFacade } from "@app/classes/infrastructure/payloadpacket/payloadpacketfacade";
import { ServerCommunicatorService } from "@app/services/server-communicator.service";
import { DashboardFilterService } from "@app/services/dashboard_filter_dependent_multiselection.service";
import { DashboardFilterStateStorageService } from "@app/services/dashboard-filter-state-storage.service";
import { LanguageService } from "@app/services/language.service";
import { TodaysTaskFetchRequest } from "@app/classes/domain/entities/website/dashboard/todaystasklist/todaystasklistfetchrequest";
import { TodaysTask } from "@app/classes/domain/entities/website/dashboard/todaystasklist/todaystasklist";

interface TodayTask {
  id: string;
  title: string;
  titleMr: string;
  count: string;
  iconImg?: string;
  emoji?: string;
  from_entity_type?: number;
}

const TASK_ICON_BY_ENTITY: Record<number, string> = {
  [FromEntityTypes.anc_register_master]: "assets/dashboard_icons/today_task_icon/anc_visit.png",
  [FromEntityTypes.anc_follow_up_master]: "assets/dashboard_icons/today_task_icon/anc_visit.png",
  [FromEntityTypes.maata_register_master]: "assets/dashboard_icons/today_task_icon/anc_visit.png",
  [FromEntityTypes.child_immunization_register_master]: "assets/dashboard_icons/today_task_icon/child_immunization_visit.png",
  [FromEntityTypes.hbnc_follow_up_master ]: "assets/dashboard_icons/today_task_icon/hbnc_visit.png",
  [FromEntityTypes.pnc_follow_up_master ]: "assets/dashboard_icons/today_task_icon/pnc_visit.png",
  [FromEntityTypes.abortion_follow_up_master]: "assets/dashboard_icons/today_task_icon/abortion_visit.png",
  [FromEntityTypes.miscarriage_follow_up_master]: "assets/dashboard_icons/today_task_icon/miscarriage_visit.png",
};

function getTodayTaskIcon(entityType?: number, idOrTitle?: string): string | undefined {
  if (entityType && TASK_ICON_BY_ENTITY[entityType]) {
    return TASK_ICON_BY_ENTITY[entityType];
  }
  const str = (idOrTitle || "").toLowerCase();
  if (str.includes("anc") || str.includes("pregnant") || str.includes("maata") || str.includes("mata") || str.includes("माता")) {
    return "assets/dashboard_icons/today_task_icon/anc_visit.png";
  }
  if (str.includes("immun") || str.includes("child") || str.includes("लसीकरण") || str.includes("बाल")) {
    return "assets/dashboard_icons/today_task_icon/child_immunization_visit.png";
  }
  if (str.includes("hbnc")) {
    return "assets/dashboard_icons/today_task_icon/hbnc_visit.png";
  }
  if (str.includes("pnc")) {
    return "assets/dashboard_icons/today_task_icon/pnc_visit.png";
  }
  if (str.includes("abort") || str.includes("गर्भपात")) {
    return "assets/dashboard_icons/today_task_icon/abortion_visit.png";
  }
  if (str.includes("miscarriage") || str.includes("गर्भस्राव")) {
    return "assets/dashboard_icons/today_task_icon/miscarriage_visit.png";
  }
  return undefined;
}

@Component({
  selector: "app-todays-task-list",
  templateUrl: "./todays-task-list.component.html",
  styleUrl: "./todays-task-list.component.scss",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    DevextremesharedmoduleModule,
    DxSelectBoxModule,
    HierarchyFilterComponent,
  ],
})
export class TodaysTaskListComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // ---------------- Filter bar state ----------------
  YearsList: FinancialYear[] = [];
  CurrentFinancialYear: string = "2026-2027";
  FinancialYearRef: number = 0;

  FilterList = DomainEnums.ReportFilterTypesListLang(false, "REPORT_PLACEHOLDERS.Select");
  MonthList = DomainEnums.MonthListLang(true, "REPORT_PLACEHOLDERS.SelectMonth");
  QuarterList = DomainEnums.QuarterTypesListLang(true, "REPORT_PLACEHOLDERS.SelectQuarter");
  HalfYearList = DomainEnums.HalfYearListLang(true, "REPORT_PLACEHOLDERS.SelectHalfYear");

  ReportFilterType: number = ReportFilterTypes.Yearly;
  Month: number = 0;
  QuarterlyYearType: number = 0;
  HalfYearType: number = 0;

  selectedArea: "rural" | "urban" = "rural";
  isUrbanNoData: boolean = false;

  IsShowMonth: boolean = false;
  IsShowQuarter: boolean = false;
  IsShowHalfYear: boolean = false;

  // ---------------- Tasks (live only – no mock) ----------------
  todayTasks: TodayTask[] = [];
  isTodayTasksLive: boolean = false;
  isLoading: boolean = false;

  get totalTodayTasksCount(): string {
    const total = this.todayTasks.reduce((sum, t) => sum + (parseInt(t.count, 10) || 0), 0);
    return String(total).padStart(2, "0");
  }

  constructor(
    public dashboardFilterService: DashboardFilterService,
    public filterStorage: DashboardFilterStateStorageService,
    public langService: LanguageService,
    private translate: TranslateService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private breadcrumbService: BreadcrumbService,
    private location: Location
  ) { }

  async ngOnInit() {
    this.updateBreadcrumb();

    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateBreadcrumb();
    });

    await this.fetchTodaysTasks();
  }

  private updateBreadcrumb(): void {
    this.breadcrumbService.set([
      { label: 'Home', labelKey: 'MENU.Dashboard', path: SUBMENU_ID.HOME },
      { label: "Today's Tasks", labelKey: 'TODAYS_TASK.ALL_TASKS_TODAY' },
    ]);
  }

  // ---------------- Fetch (same API as dashboard) ----------------
  private clearTodayTasks(): void {
    this.todayTasks = [];
    this.isTodayTasksLive = false;
    this.cdr.markForCheck();
  }

  fetchTodaysTasks = async () => {
    this.isLoading = true;
    this.cdr.markForCheck();
    try {
      const req = new TodaysTaskFetchRequest();
      const f = this.dashboardFilterService;
      if (f.state_ids.length) req.state_ids = f.state_ids;
      if (f.zone_ids.length) req.zone_ids = f.zone_ids;
      if (f.district_ids.length) req.district_ids = f.district_ids;
      if (f.block_ids.length) req.block_ids = f.block_ids;
      if (f.phc_ids.length) req.phc_ids = f.phc_ids;
      if (f.sc_ids.length) req.sc_ids = f.sc_ids;
      if (f.village_ids.length) req.village_ids = f.village_ids;
      if (f.asha_ids.length) req.asha_employee_record_ids = f.asha_ids;

      const pkt = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(req.FormulateTransportData());
      const tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pkt);

      if (!tr?.Successful) {
        this.clearTodayTasks();
        return;
      }

      const res = TodaysTask.FromTransportData(JSON.parse(tr.Tag) as TransportData);
      console.log("[TodayTasksList] res:", res);

      if (!res?.length) {
        this.clearTodayTasks();
        return;
      }

      const pad = (v: number) => String(v).padStart(2, "0");

      this.todayTasks = [...res]
        .sort((a, b) => (Number(a.display_order) || 0) - (Number(b.display_order) || 0))
        .map((item) => {
          const count = Number(item.total_count) || 0;
          const id = (item.name_en || "task").toLowerCase().replace(/\s+/g, "_");
          const entityType = Number(item.from_entity_type) || 0;

          return {
            id,
            title: item.name_en || "Task",
            titleMr: item.name_mr || item.name_en || "काम",
            count: pad(count),
            iconImg: getTodayTaskIcon(entityType, item.name_en || id),
            emoji: "📋",
            from_entity_type: entityType,
          } as TodayTask;
        })
        .filter((x) => Number(x.count) > 0);

      this.isTodayTasksLive = true;
    } catch (err) {
      console.warn("[TodaysTaskList] fetch failed:", err);
      this.clearTodayTasks();
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  };

  // ---------------- Filters ----------------
  async openFilterPanel(): Promise<void> {
    this.dashboardFilterService.openFilter();
    await this.dashboardFilterService.LoadFilterDataUPTOStateLevel();
  }

  onYearChanged(e: any) {
    if (!e.value) return;
    this.FinancialYearRef = e.value;
    this.filterStorage.setFilterState({ FinancialYearRef: this.FinancialYearRef });

    const selectedYear = this.YearsList.find((y) => y.p.id === e.value);
    if (selectedYear) this.CurrentFinancialYear = selectedYear.p.short_name;
  }

  getTranslatedName = (item: any): string => {
    if (!item) return "";
    return item.Name ? this.translate.instant(item.Name) : (item.name || item.display || item.toString());
  };

  OnFilterChange = (filterRef: number) => {
    if (!filterRef) return;
    this.ReportFilterType = filterRef;
    this.Month = 0;
    this.QuarterlyYearType = 0;
    this.HalfYearType = 0;
    this.applyFilterVisibility(filterRef);
    this.filterStorage.setFilterState({
      ReportFilterType: filterRef,
      Month: 0,
      QuarterlyYearType: 0,
      HalfYearType: 0,
    });
  };

  onMonthChanged = (e: any) => {
    if (!e.value) return;
    this.Month = e.value;
    this.filterStorage.setFilterState({ Month: this.Month });
  };

  onQuarterChanged = (e: any) => {
    if (!e.value) return;
    this.QuarterlyYearType = e.value;
    this.filterStorage.setFilterState({ QuarterlyYearType: this.QuarterlyYearType });
  };

  onHalfYearChanged = (e: any) => {
    if (!e.value) return;
    this.HalfYearType = e.value;
    this.filterStorage.setFilterState({ HalfYearType: this.HalfYearType });
  };

  applyFilterVisibility(filterType: number) {
    this.IsShowMonth = filterType === ReportFilterTypes.Monthly;
    this.IsShowQuarter = filterType === ReportFilterTypes.Quarterly;
    this.IsShowHalfYear = filterType === ReportFilterTypes.HalfYearly;
  }

  onAreaChange(area: "rural" | "urban") {
    if (this.selectedArea === area) return;
    this.selectedArea = area;
    this.isUrbanNoData = area === "urban";
    if (area === "rural") this.fetchTodaysTasks();
  }

  onFiltersApplied = async () => {
    this.filterStorage.setFilterState({
      FinancialYearRef: this.FinancialYearRef,
      ReportFilterType: this.ReportFilterType,
      Month: this.Month,
      QuarterlyYearType: this.QuarterlyYearType,
      HalfYearType: this.HalfYearType,
      state_ids: this.dashboardFilterService.state_ids,
      zone_ids: this.dashboardFilterService.zone_ids,
      district_ids: this.dashboardFilterService.district_ids,
      block_ids: this.dashboardFilterService.block_ids,
      bcm_post_ids: this.dashboardFilterService.bcm_post_ids,
      phc_ids: this.dashboardFilterService.phc_ids,
      af_ids: this.dashboardFilterService.af_ids,
      sc_ids: this.dashboardFilterService.sc_ids,
      village_ids: this.dashboardFilterService.village_ids,
      asha_ids: this.dashboardFilterService.asha_ids,
      asha_employee_record_ids: this.dashboardFilterService.asha_ids,
    });
    await this.fetchTodaysTasks();
  };

  onFiltersCleared = () => { };

  // ---------------- Navigation ----------------
  async onTaskClick(taskId: string) {
    const task = this.todayTasks.find((t) => t.id === taskId);
    if (!task) return;

    const nav = {
      taskId: task.id,
      title: task.title || "",
      titleMr: task.titleMr || "",
      from_entity_type: task.from_entity_type || 0,
    };

    sessionStorage.setItem("todaysTaskDetailsNav", JSON.stringify(nav));

    await this.router.navigate([SUBMENU_ID.TODAYS_TASK_LIST_DETAILS], {
      state: nav,
    });
  }

  async BackAsha() {
    if (window.history.length > 1) {
      this.location.back();
    } else {
      await this.router.navigate([SUBMENU_ID.DASHBOARD]);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
